import { NotificationContentType } from "@/types/notifications";
import { useMemo } from "react";
import { TabContentKey } from "./useGetNotificationList";
import { useInfiniteNotificationList } from "./useInfiniteNotificationList";
import { useLocale } from "@/hooks/useLocale";

const labelByContentKey: Record<TabContentKey, string> = {
  ContractAndOperationManagement: "계약・ 광고 관리",
  ServiceUsageDetail: "서비스 이용",
  Announcement: "공지사항",
};

export type TabValue =
  | "all"
  | "contractAndOperationManagement"
  | "serviceUsageDetail"
  | "announcement";

const valueToContentKey: Record<Exclude<TabValue, "all">, TabContentKey> = {
  contractAndOperationManagement: "ContractAndOperationManagement",
  serviceUsageDetail: "ServiceUsageDetail",
  announcement: "Announcement",
};

type Enriched = NotificationContentType & {
  _type_lc?: string;
  _mapped_tab?: TabContentKey;
  titleToShow?: string;
};

const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};
const monthsAgo = (n: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d;
};

const inWindow = (iso: string, tab: TabValue) => {
  const created = new Date(iso);
  const cutoff = tab === "all" ? daysAgo(15) : monthsAgo(6);
  return created >= cutoff;
};

export const useNotificationDataInfinite = (tab: TabValue) => {
  const locale = useLocale(); // Get current locale from cookie

  const baseParams = useMemo(
    () => ({
      page_size: 20,
      sort_order: "newest",
      ...(tab !== "all" && {
        search_types: valueToContentKey[tab as Exclude<TabValue, "all">],
      }),
    }),
    [tab]
  );

  // Infinite list cho UI (includes locale in queryKey)
  const listQ = useInfiniteNotificationList(baseParams, locale);

  const flatList: Enriched[] = useMemo(() => {
    const idSet = new Set<number | string>();
    const items: Enriched[] = [];
    for (const p of listQ.data?.pages ?? []) {
      // API returns { data: { notifications: [...] } }
      const pageData = p?.data;
      const arr = Array.isArray(pageData?.notifications)
        ? pageData.notifications
        : [];
      for (const n of arr) {
        const key = n.id;
        if (idSet.has(key)) continue;
        idSet.add(key);
        items.push({ ...n });
      }
    }

    return items;
  }, [listQ.data]);

  const windowedFlatList = useMemo(() => {
    const filtered = flatList.filter((n) => inWindow(n.created_at, tab));
    return filtered;
  }, [flatList, tab]);
  // Prefer server-provided counts when available (backend uses PascalCase keys)
  const unreadCounts = useMemo(() => {
    // Backend may return counts under different keys or nested under `data.count`.
    // Try to find any of these shapes in the first page.
    const firstPage = listQ.data?.pages?.[0] as unknown;

    // With new structure: { data: { notifications: [...], count: {...} } }
    const fromServer = (
      firstPage as { data?: { count?: Record<string, number> } }
    )?.data?.count as Record<string, number> | undefined;

    if (fromServer && typeof fromServer === "object") {
      const counts: Record<"all" | TabContentKey, number> = {
        all: 0,
        ContractAndOperationManagement: 0,
        ServiceUsageDetail: 0,
        Announcement: 0,
      };

      // backend may return keys like: all, All, ContractAndOperationManagement, ServiceUsageDetail, Announcement
      const mapping: Record<string, keyof typeof counts> = {
        all: "all",
        All: "all",
        ContractAndOperationManagement: "ContractAndOperationManagement",
        ServiceUsageDetail: "ServiceUsageDetail",
        Announcement: "Announcement",
      };

      for (const [k, v] of Object.entries(fromServer)) {
        const mapped = mapping[k] ?? null;
        if (mapped) {
          const key = mapped as keyof typeof counts;
          counts[key] = Number(v) || 0;
        }
      }

      return counts;
    }

    // Fallback: compute from client-side list as before
    const counts: Record<"all" | TabContentKey, number> = {
      all: 0,
      ContractAndOperationManagement: 0,
      ServiceUsageDetail: 0,
      Announcement: 0,
    };
    for (const n of windowedFlatList) {
      if (!n.read_at) {
        counts.all += 1;
        const mapped = n.type;
        if (mapped) counts[mapped as TabContentKey] += 1;
      }
    }
    return counts;
  }, [windowedFlatList, listQ.data]);

  const filteredList = useMemo(() => {
    // When tab !== "all", the API already filters by search_types,
    // so we don't need to filter again on the client side
    return windowedFlatList;
  }, [windowedFlatList]);

  const computedItems = useMemo(() => {
    let result;
    if (tab !== "all") {
      const label = labelByContentKey[valueToContentKey[tab]];
      result = filteredList.map((n) => ({ ...n, titleToShow: label }));
    } else {
      result = filteredList.map((n) => {
        const title = n.title;
        return { ...n, titleToShow: title };
      });
    }

    return result;
  }, [tab, filteredList]);

  return {
    list: computedItems,
    unreadCounts,
    hasNextPage: listQ.hasNextPage,
    fetchNextPage: listQ.fetchNextPage,
    isFetchingNextPage: listQ.isFetchingNextPage,
    isLoading: listQ.isLoading,
    isFetching: listQ.isFetching,
    error: listQ.error,
  };
};
